import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Droplet, Thermometer, Gauge, ChevronRight } from 'lucide-react';
import SettingCard from './SettingCard';
import { panelLabel } from '../../utils/panelLabels';

// Channel colours as the panel sets them.
const INLET = '#5cb85c';
const OUTLET = '#428bca';
const RATE = '#f0ad4e';
const DRYING = '#d9534f';

const limitsFor = (prefix) => [
  { name: `${prefix}_critical_low`, label: 'Critical Low' },
  { name: `${prefix}_critical_high`, label: 'Critical High' },
  { name: `${prefix}_warning_low`, label: 'Warning Low' },
  { name: `${prefix}_warning_high`, label: 'Warning High' }
];

// The panel picks the alarm first, then shows its limits. Same shape here:
// a chooser, then one page per alarm.
const alarms = [
  { id: 'inlet_moisture', title: 'Inlet moisture', icon: Droplet, color: INLET },
  { id: 'inlet_temperature', title: 'Inlet temperature', icon: Thermometer, color: INLET },
  { id: 'outlet_moisture', title: 'Outlet moisture', icon: Droplet, color: OUTLET },
  { id: 'outlet_temperature', title: 'Outlet temperature', icon: Thermometer, color: OUTLET },
  { id: 'discharge_rate', title: 'Discharge rate', icon: Gauge, color: RATE },
  { id: 'drying_temperature', title: 'Drying temperature', icon: Thermometer, color: DRYING }
].map((alarm) => ({ ...alarm, settings: limitsFor(alarm.id) }));

const AlarmSettings = ({ getSettingValue, handleEdit, onBack }) => {
  const [selected, setSelected] = useState(null);

  const handleBack = () => {
    if (selected) {
      setSelected(null);
    } else if (onBack) {
      onBack();
    }
  };

  if (selected) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
          </button>
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${selected.color}1a` }}
          >
            <selected.icon className="w-5 h-5" style={{ color: selected.color }} />
          </div>
          <h3 className="text-xl font-bold text-gray-800 capitalize">{selected.title}</h3>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4 max-w-3xl">
            {selected.settings.map((setting) => {
              const settingData = getSettingValue(setting.name);
              return (
                <SettingCard
                  key={setting.name}
                  name={panelLabel(setting.name, setting.label)}
                  value={settingData.value}
                  min={settingData.minRange}
                  max={settingData.maxRange}
                  onEdit={(_, value) => handleEdit(setting.name, value)}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
        {onBack && (
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
          </button>
        )}
        <h3 className="text-xl font-bold text-gray-800">Alarm Configuration</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {alarms.map((alarm) => (
            <motion.button
              key={alarm.id}
              onClick={() => setSelected(alarm)}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.99 }}
              className="relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden text-left flex items-center gap-4 p-5"
            >
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${alarm.color}1a` }}
              >
                <alarm.icon className="w-6 h-6" style={{ color: alarm.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-semibold text-gray-800 capitalize">{alarm.title}</p>
                <p className="text-sm text-gray-500">Set limits</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
              <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: alarm.color }} />
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlarmSettings;
